import {
	Box,
	CircularProgress,
	Stack,
	Divider,
	Grid,
	useTheme,
} from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import {
	getExistingJournalEntries,
	handleDeleteJournalChat,
} from '../../utils/api';
import EmptyOrDefaultListHeader from './journalingHeaders/EmptyOrDefaultListHeader';
import ViewEntryHeader from './journalingHeaders/ViewEntryHeader';
import EditEntryHeader from './journalingHeaders/EditEntryHeader';
import JournalList from './JournalList';
import JournalDetailsEditForm, { JournalEntryDetailsRef } from './JournalDetailsEditForm';
import EmptyListPage from './EmptyListPage';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import DetectScreenSize from './DetectScreenSize';
import { toast } from 'react-toastify';

const PAGE_LIMIT = 20;

const ScreenModes = {
	EMPTY_LIST: 'emptyList',
	DEFAULT_LIST: 'defaultList',
	VIEW_ENTRY: 'viewEntry',
	EDIT_ENTRY: 'editEntry',
};

const ScreenSizes = {
	XS: 'xs',
	SM: 'sm',
	MD: 'md',
	LG: 'lg',
	XL: 'xl',
};

const Journaling = () => {
	const { authUser } = useAuth();
	const [selectedJournalEntry, setSelectedJournalEntry] = useState(null);
	const [isDeleting, setIsDeletingEntry] = useState(false);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [isSaveEnabled, setIsSaveEnabled] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [hasAutoSelectedFirst, setHasAutoSelectedFirst] = useState(false);
	let saveDetailRef = null;
	const loadingRef = useRef(null);
	const [screenMode, setScreenMode] = useState(ScreenModes.DEFAULT_LIST);
	const screenSize = DetectScreenSize();
	const theme = useTheme();
	const navigate = useNavigate();
	const isScreenMdOrLarger = [
		ScreenSizes.MD,
		ScreenSizes.LG,
		ScreenSizes.XL,
	].includes(screenSize);

	const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
		useInfiniteQuery({
			queryKey: ['journalEntries', authUser?.uid],
			queryFn: async ({ pageParam = 1 }) => {
				try {
					const resp = await getExistingJournalEntries(
						authUser?.uid,
						pageParam,
						PAGE_LIMIT
					);
					if (resp.ok) {
						const data = await resp.json();
						return {
							entries: data.history || [],
							pagination: data.pagination,
						};
					}
					return { entries: [], pagination: null };
				} catch (err) {
					return { entries: [], pagination: null };
				}
			},
			initialPageParam: 1,
			getNextPageParam: (lastPage) => {
				const nextPage = lastPage.pagination?.has_next
					? lastPage.pagination.current_page + 1
					: undefined;
				return nextPage;
			},
			enabled: !!authUser?.uid,
		});

	const journalEntries = data?.pages.flatMap((page) => page.entries) || [];
	const hasFetchedData = data !== undefined;

	useEffect(() => {
		if (
			!hasAutoSelectedFirst &&
			journalEntries.length > 0 &&
			!isLoading &&
			hasFetchedData
		) {
			const firstEntry = { ...journalEntries[0], selected: true };
			setSelectedJournalEntry(firstEntry);
			setHasAutoSelectedFirst(true);
		}
	}, [journalEntries, isLoading, hasFetchedData, hasAutoSelectedFirst]);

	const postJournalEntry = async (authUserId, journalId) => {
		try {
			let resp = await handleDeleteJournalChat(authUserId, journalId);
			if (resp) {
				toast.success('Deleted journal entry successfully');
				setIsDeletingEntry(false);
				navigate(0);
			} else {
				toast.error('Unable to save journal entry');
			}
		} catch (err) {
			setIsDeletingEntry(false);
		}
	};

	const handleBackClick = () => {
		if (screenMode === ScreenModes.VIEW_ENTRY) {
			if (!isScreenMdOrLarger) {
				setScreenMode(ScreenModes.DEFAULT_LIST);
			}
		} else if (screenMode === ScreenModes.EDIT_ENTRY) {
			if (isScreenMdOrLarger) {
				setScreenMode(ScreenModes.DEFAULT_LIST);
			} else {
				setScreenMode(ScreenModes.VIEW_ENTRY);
			}
		}
	};

	const handleJournalTitleClick = (entry) => {
		setSelectedJournalEntry(entry);
		if (!isScreenMdOrLarger) {
			setScreenMode(ScreenModes.VIEW_ENTRY);
		}
	};

	const handleNewEntryClick = () => {
		navigate(`/${authUser?.userName}/update/journaling/newEntry`);
	};

	const handleEditClick = () => {
		setScreenMode(ScreenModes.EDIT_ENTRY);
	};

	const handleDeleteClick = () => {
		setShowDeleteDialog(true);
	};

	const handleConfirmedDeleteClick = async (e) => {
		e.preventDefault();
		setIsDeletingEntry(true);
		try {
			await postJournalEntry(authUser?.uid, selectedJournalEntry.docId);
		} catch (error) {
			console.log(error);
		}
	};

	const detailsRef = useRef<JournalEntryDetailsRef>(null);

	const handleSave = () => {
		if (detailsRef.current) {
			detailsRef.current.saveDetails();
		}
	};

	const shouldShowEmptyState =
		journalEntries.length === 0 && !isLoading && hasFetchedData;

	return (
		<Box
			sx={{
				height: { xs: `calc(100vh - 106px)`, md: '100vh' },
				margin: `calc(-3 * var(--mui-spacing))`,
				marginBottom: `calc(-9 * var(--mui-spacing))`,
				overflow: 'hidden',
				scrollbarWidth: 'none',
				'&::-webkit-scrollbar': { display: 'none' },
			}}
		>
			<Stack
				direction='column'
				sx={{
					width: '100%',
					height: { xs: `calc(100vh - 106px)`, md: '100vh' },
					margin: `auto`,
					maxWidth: 'lg',
					overflow: 'hidden',
					scrollbarWidth: 'none',
					'&::-webkit-scrollbar': { display: 'none' },
				}}
			>
				<>
					{screenMode === ScreenModes.EMPTY_LIST ||
					screenMode === ScreenModes.DEFAULT_LIST ? (
						<EmptyOrDefaultListHeader
							onNewEntryClick={handleNewEntryClick}
							isScreenMdOrLarger={isScreenMdOrLarger}
						/>
					) : screenMode === ScreenModes.VIEW_ENTRY ? (
						isScreenMdOrLarger ? (
							<EmptyOrDefaultListHeader
								onNewEntryClick={handleNewEntryClick}
								isScreenMdOrLarger={isScreenMdOrLarger}
							/>
						) : (
							<ViewEntryHeader
								handleBackClick={handleBackClick}
								handleEditClick={handleEditClick}
							/>
						)
					) : screenMode === ScreenModes.EDIT_ENTRY ? (
						<EditEntryHeader
							handleBackClick={handleBackClick}
							isSaveEnabled={isSaveEnabled}
							isSaving={isSaving}
							handleSave={handleSave}
						/>
					) : (
						<></>
					)}
				</>
				<Divider sx={{ borderColor: 'divider' }} />
				<>
					{isLoading && journalEntries.length === 0 ? (
						<Box sx={{ margin: 'auto' }}>
							<CircularProgress size={30} />
						</Box>
					) : shouldShowEmptyState ? (
						<EmptyListPage onNewEntryClick={handleNewEntryClick} />
					) : (
						<Box
							sx={{
								width: '100%',
								flex: 1,
								p: '20px',
								maxHeight: isScreenMdOrLarger
									? `calc(100vh - 56px)`
									: `calc(100vh - 162px)`,
							}}
						>
							{isScreenMdOrLarger ? (
								<Grid
									container
									direction='row'
									height='100%'
									style={{ flexGrow: 1 }}
								>
									<JournalList
										journalEntries={journalEntries}
										isLoading={isFetching}
										isFullyLoaded={!hasNextPage}
										selectedJournalEntry={selectedJournalEntry}
										handleJournalTitleClick={handleJournalTitleClick}
										fetchNextPage={fetchNextPage}
										hasNextPage={hasNextPage}
										loadingRef={loadingRef}
										isScreenMdOrLarger={isScreenMdOrLarger}
									/>
									{selectedJournalEntry && (
										<JournalDetailsEditForm
											ref={detailsRef}
											entry={selectedJournalEntry}
											screenMode={screenMode}
											handleEditClick={handleEditClick}
											authUserId={authUser?.uid}
											isSaveEnabled={isSaveEnabled}
											setIsSaveEnabled={setIsSaveEnabled}
											isSaving={isSaving}
											setIsSaving={setIsSaving}
											isScreenMdOrLarger={isScreenMdOrLarger}
											isDeleting={isDeleting}
											handleDeleteClick={() => setShowDeleteDialog(true)}
										/>
									)}
								</Grid>
							) : (
								<Grid container direction='row' height='100%'>
									{screenMode === ScreenModes.DEFAULT_LIST ? (
										<JournalList
											journalEntries={journalEntries}
											isLoading={isFetching}
											isFullyLoaded={!hasNextPage}
											selectedJournalEntry={selectedJournalEntry}
											handleJournalTitleClick={handleJournalTitleClick}
											fetchNextPage={fetchNextPage}
											hasNextPage={hasNextPage}
											loadingRef={loadingRef}
											isScreenMdOrLarger={isScreenMdOrLarger}
										/>
									) : screenMode === ScreenModes.VIEW_ENTRY ||
									  screenMode === ScreenModes.EDIT_ENTRY ? (
										selectedJournalEntry && (
											<JournalDetailsEditForm
												ref={detailsRef}
												entry={selectedJournalEntry}
												screenMode={screenMode}
												handleEditClick={handleEditClick}
												authUserId={authUser?.uid}
												isSaveEnabled={isSaveEnabled}
												setIsSaveEnabled={setIsSaveEnabled}
												isSaving={isSaving}
												setIsSaving={setIsSaving}
												isScreenMdOrLarger={isScreenMdOrLarger}
												isDeleting={isDeleting}
												handleDeleteClick={() => setShowDeleteDialog(true)}
											/>
										)
									) : (
										<></>
									)}
								</Grid>
							)}
						</Box>
					)}
				</>
			</Stack>
			<ConfirmDeleteModal
				isDeleting={isDeleting}
				handleCloseClick={() => setShowDeleteDialog(false)}
				handleConfirmedDeleteClick={handleConfirmedDeleteClick}
				showDeleteDialog={showDeleteDialog}
			/>
		</Box>
	);
};

export default Journaling;
